import { toZonedTime } from 'date-fns-tz';

const TIMEZONE = 'Europe/Berlin';

type DiscountCodeForValidation = {
    name: string;
    startDate: string | Date;
    endDate: string | Date;
    validDaysOfWeek: number[];
    validTimeWindowStart: string | null;
    validTimeWindowEnd: string | null;
    applicationCustomerType: 'NEW_CUSTOMERS_ONLY' | 'RETURNING_CUSTOMERS_ONLY' | 'ALL_CUSTOMERS';
};

type CustomerType = 'NEW' | 'RETURNING' | null;

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
    NEW_CUSTOMERS_ONLY: 'Neukunden',
    RETURNING_CUSTOMERS_ONLY: 'Bestandskunden',
    ALL_CUSTOMERS: 'alle Kunden',
};

/**
 * Checks all discount code eligibility conditions and returns an array of warning strings.
 * Empty array = all conditions met, code can be applied without warning.
 */
export function getDiscountCodeWarnings(
    code: DiscountCodeForValidation,
    appointmentStartTime: string | null | undefined,
    customerType: CustomerType,
): string[] {
    const warnings: string[] = [];

    // 1. Date validity (day-level comparison in Europe/Berlin)
    const nowInBerlin = toZonedTime(new Date(), TIMEZONE);
    const startInBerlin = toZonedTime(new Date(code.startDate), TIMEZONE);
    const endInBerlin = toZonedTime(new Date(code.endDate), TIMEZONE);

    const toDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayStr = toDateStr(nowInBerlin);
    const startStr = toDateStr(startInBerlin);
    const endStr = toDateStr(endInBerlin);

    if (todayStr < startStr) {
        const formatted = startInBerlin.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        warnings.push(`Gültigkeitszeitraum beginnt erst am ${formatted}`);
    } else if (todayStr > endStr) {
        const formatted = endInBerlin.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        warnings.push(`Gültigkeitszeitraum ist am ${formatted} abgelaufen`);
    }

    // 2. Day-of-week + time window
    if (appointmentStartTime) {
        const zonedDate = toZonedTime(new Date(appointmentStartTime), TIMEZONE);

        if (code.validDaysOfWeek.length > 0) {
            const jsDay = zonedDate.getDay();
            const isoWeekday = jsDay === 0 ? 7 : jsDay;
            if (!code.validDaysOfWeek.includes(isoWeekday)) {
                const dayNames = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
                const validDays = code.validDaysOfWeek.map(d => dayNames[d]).join(', ');
                warnings.push(`Nur an bestimmten Tagen gültig (${validDays})`);
            }
        }

        if (code.validTimeWindowStart && code.validTimeWindowEnd) {
            const h = String(zonedDate.getHours()).padStart(2, '0');
            const m = String(zonedDate.getMinutes()).padStart(2, '0');
            const timeStr = `${h}:${m}`;
            if (timeStr < code.validTimeWindowStart || timeStr > code.validTimeWindowEnd) {
                warnings.push(`Nur im Zeitfenster ${code.validTimeWindowStart}\u2013${code.validTimeWindowEnd} gültig`);
            }
        }
    }

    // 3. Customer type eligibility
    if (code.applicationCustomerType !== 'ALL_CUSTOMERS' && customerType) {
        const isEligible =
            (code.applicationCustomerType === 'NEW_CUSTOMERS_ONLY' && customerType === 'NEW') ||
            (code.applicationCustomerType === 'RETURNING_CUSTOMERS_ONLY' && customerType === 'RETURNING');

        if (!isEligible) {
            const targetLabel = CUSTOMER_TYPE_LABELS[code.applicationCustomerType];
            const actualLabel = customerType === 'NEW' ? 'ein Neukunde' : 'ein Bestandskunde';
            warnings.push(`Nur für ${targetLabel} vorgesehen, Kunde ist ${actualLabel}`);
        }
    }

    return warnings;
}

export function formatDiscountCodeWarningMessage(codeName: string, warnings: string[]): string {
    if (warnings.length === 1) {
        return `Dieser Rabattcode "${codeName}" hat folgendes Problem:\n\n\u2022 ${warnings[0]}\n\nMöchten Sie den Rabattcode trotzdem anwenden?`;
    }
    return `Dieser Rabattcode "${codeName}" hat folgende Probleme:\n\n${warnings.map(w => `\u2022 ${w}`).join('\n')}\n\nMöchten Sie den Rabattcode trotzdem anwenden?`;
}
